// src/services/questApi.ts
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import {
  claimMovedMoney,
  claimOutcome,
  claimOutcomeFromError,
  type ClaimOutcome,
} from '@/lib/missions/claim';
import { getQuestAccessToken } from '@/lib/logto';
import { QBANK_WALLET_TAG, qbankApi } from '@/services/qbankApi';
import type { ClaimSuccess, MissionsResponse } from '@/types/quest';

/**
 * Client di Quest — il servizio missioni & engagement.
 *
 * Auth: Bearer da `getQuestAccessToken()`, audience `https://quest.qlash.gg/api`.
 * NESSUN ramo `X-Dev-Bypass`: su Quest il bypass vive solo con
 * `NODE_ENV === 'development'` e la produzione gira in `production`
 * (`quest-api/src/auth/authMiddleware.ts:22` + `config.ts:5` @ `b24b03d`;
 * verificato in vivo, 401 anche col bypass). Scriverne uno sarebbe codice morto
 * che promette una scorciatoia inesistente.
 *
 * Dev: `env.questApiUrl = '/quest-api'`, proxy Vite. Prod: `https://quest.qlash.gg`.
 *
 * ⚠️ DUE ROTTE, E LA SECONDA MUOVE DENARO. Fino al 2026-08-29 questo client era
 * di sola lettura; dal 30/8 porta anche il claim (ordine di Luca: *«la riscossione
 * dei premi missione dovrebbe sempre avere bottone incassa»*, e alla domanda «TMA
 * ora o resta al bot?» → *«1 tma ora»*). Restano fuori, e non per fase:
 * `POST /internal/events` e' M2M (`quest:events:ingest`), si chiama `/internal/`
 * apposta, e un evento emesso dal client sarebbe un rubinetto di essenze
 * pilotabile dall'utente. Il confine non e' "lettura contro scrittura", e' "cio'
 * che il giocatore ha diritto di chiedere per se'".
 */
export const questApi = createApi({
  reducerPath: 'questApi',
  baseQuery: retry(
    fetchBaseQuery({
      baseUrl: env.questApiUrl,
      prepareHeaders: async (headers) => {
        const token = await getQuestAccessToken();
        if (token) headers.set('Authorization', `Bearer ${token}`);
        headers.set('x-platform-id', env.platformId);
        headers.set('Accept', 'application/json');
        return headers;
      },
    }),
    {
      // Un 401 o un 403 non si ritentano: sono verdetti, non guasti passeggeri.
      // 403 in particolare significa "scope mancante" e riprovare produrrebbe lo
      // stesso 403 con lo stesso token — mentre il corpo (`{"missing":[...]}`)
      // dice gia' quale scope manca. Si ritenta solo cio' che puo' cambiare da
      // solo: rete caduta, timeout, 5xx.
      //
      // 🔴 E NIENTE DI CIO' CHE NON SIA UNA LETTURA. Dal 2026-08-30 questo client
      // ha una rotta di SCRITTURA che muove denaro (il claim), e un ritentativo
      // automatico su un `POST` significa che una sola pressione dell'utente parte
      // due volte. Lato server il claim e' idempotente (indice unico su
      // `Payout.instanceId`), quindi il danno non sarebbe un doppio pagamento — ma
      // la seconda chiamata tornerebbe `409 already_claimed` e sarebbe la PRIMA
      // risposta a essere buttata via: l'utente vedrebbe l'esito del replay al
      // posto del proprio. E soprattutto: la garanzia che regge tutto questo vive
      // in un ALTRO servizio, e non e' cio' su cui va appoggiata una decisione
      // presa qui. La guardia e' sul metodo e non sul nome della rotta, cosi' vale
      // per ogni scrittura futura senza che nessuno debba ricordarsene.
      retryCondition: (error, args, { attempt }) => {
        const method =
          typeof args === 'object' && args !== null && 'method' in args
            ? (args as { method?: string }).method
            : undefined;
        if (method !== undefined && method !== 'GET') return false;
        if (attempt > 1) return false;
        const status = (error as { status?: number | string }).status;
        if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') return true;
        return typeof status === 'number' && status >= 500;
      },
    },
  ),
  keepUnusedDataFor: 60,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: ['Missions'],
  endpoints: (builder) => ({
    /**
     * `GET /v1/missions` → `{ missions: [...] }` — le istanze del chiamante per
     * il giorno corrente, la settimana ISO corrente e tutti i traguardi, in un
     * array solo (`missions.routes.ts:7` @ `b24b03d`). Nessuna paginazione,
     * nessun filtro, e nessun campo che dica a quale finestra appartiene una
     * riga: v. la nota in cima a `@/types/quest`.
     *
     * L'identita' e' il `sub` del token: la rotta non prende parametri e non
     * esiste modo di chiedere le missioni di un altro.
     */
    getMissions: builder.query<MissionsResponse, void>({
      query: () => '/v1/missions',
      providesTags: ['Missions'],
    }),

    /**
     * `POST /v1/missions/:instanceId/claim` — la riscossione.
     *
     * 🔴 IL PRIMO GESTO DI QUESTA APP CHE MUOVE DENARO. Scope `quest:missions:claim`
     * (`server.ts:57` @ `f64873a`), diverso da quello della lettura: senza, la
     * chiamata torna `403 {"missing":["quest:missions:claim"]}` e nient'altro.
     *
     * L'identita' e' il `sub` VERIFICATO del token, mai il path ne' il corpo — il
     * claim dell'istanza di un altro e' indistinguibile da quello di un'istanza che
     * non esiste (entrambi `404`), di proposito. Nessun corpo da mandare: tutto cio'
     * che serve e' l'id nel path piu' il token.
     *
     * Idempotente lato server: la riga `Payout` viene creata PRIMA che il denaro si
     * muova, e il suo indice unico su `instanceId` e' il cancello atomico su cui due
     * riscossioni concorrenti si scontrano — mai un controllo applicativo, sempre un
     * vincolo del database. La seconda torna `409 already_claimed`, che qui e' un
     * SUCCESSO (v. `claimOutcome` in `@/lib/missions/claim`).
     */
    claimMission: builder.mutation<ClaimSuccess, string>({
      query: (instanceId) => ({
        // `encodeURIComponent` sull'id: e' un ObjectId esadecimale e non ha nulla da
        // codificare, ma arriva da una risposta di rete e finisce dentro un path —
        // il posto sbagliato in cui fidarsi di una forma attesa.
        url: `/v1/missions/${encodeURIComponent(instanceId)}/claim`,
        method: 'POST',
      }),
      /**
       * 🔴 SI RICARICA LA LISTA, NON SI AGGIORNA A MANO. La regola e' una sola — mai
       * mostrare come riscossa una riga per cui il server non ha risposto `ok` — e un
       * aggiornamento ottimistico e' letteralmente la sua violazione: scriverebbe
       * `state: 'claimed'` nella cache PRIMA della risposta. Ma anche la versione
       * pessimistica (scrivere la cache DOPO l'ok) sarebbe sbagliata qui, e per un
       * motivo che si vede solo guardando i dati: la risposta del claim non porta
       * `claimedAt`, quindi per far comparire «Riscossa il …» dovremmo INVENTARE una
       * data. `completionStamp` stamperebbe un timestamp che nessuno ha mai scritto.
       * Il refetch la prende da Quest, dove e' stata davvero stampigliata.
       *
       * Il prezzo e' una finestra fra l'ok e la lista nuova, in cui la riga direbbe
       * ancora «da riscuotere» con il bottone acceso. Quella finestra la copre la
       * riga stessa mostrando il proprio esito locale (v. `MissionRow`) — che e'
       * presentazione, non un dato scritto nella cache.
       */
      invalidatesTags: (result, error) => {
        if (result) return ['Missions'];
        const status = (error as { status?: number | string } | undefined)?.status;
        // 409 e 410 dicono, ognuno a modo suo, che la nostra copia e' vecchia
        // (`already_claimed`, `not_completed`, `expired`): la lista va ripresa. 404
        // idem. Il 503 NO, ed e' la parte che conta: l'istanza e' rimasta
        // `completed` con `claimedAt: null` — non e' cambiato nulla da ricaricare, e
        // un refetch inutile su una tesoreria vuota e' solo rumore su una rotta che
        // in quel momento e' gia' in difficolta'.
        return status === 404 || status === 409 || status === 410 ? ['Missions'] : [];
      },
      /**
       * 🔴 IL CLAIM HA DUE CONSUMATORI A VALLE, E `invalidatesTags` NE RAGGIUNGE UNO SOLO.
       *
       * Trovato dal primo claim vero della piattaforma (2026-08-30, 11:19:14Z): il denaro si e'
       * mosso alla prima e senza errori, e l'utente ha comunque scritto *«fatto reclaim,
       * confermato, ma non vedo l'essenza fra le mie valute wallet»*. Il saldo vive in `qbankApi`
       * — un altro client, con `keepUnusedDataFor: 120` e `refetchOnFocus: false` — e nessuno gli
       * diceva nulla. Fino a due minuti di saldo vecchio dopo una riscossione riuscita, che alla
       * prima impressione sul denaro si legge come «non ha funzionato».
       *
       * ⚠️ Il commento su `invalidatesTags` qui sopra ragionava sulla finestra fra l'ok e la
       * LISTA, ed era giusto. Il punto cieco era un altro: `invalidatesTags` puo' invalidare solo
       * tag di QUESTO client, e il tag che serviva stava dall'altra parte del confine. Da qui il
       * `dispatch` esplicito — non c'e' un modo dichiarativo di attraversarlo.
       *
       * Perche' `onQueryStarted` e non un `.then()` nel componente: il fatto «e' stato riscosso un
       * premio» appartiene alla mutation, non alla riga che l'ha premuta. Una seconda superficie
       * che un giorno riscuotesse (l'hub, una notifica) erediterebbe questa conseguenza senza
       * doversene ricordare — ed e' esattamente il tipo di cosa che nessuno si ricorda.
       *
       * L'esito si rilegge con la STESSA mappa che usa la UI (`claimOutcome`), non con un secondo
       * `if` sullo status: due letture della stessa risposta divergerebbero al primo caso nuovo, e
       * divergerebbero in silenzio.
       */
      async onQueryStarted(_instanceId, { dispatch, queryFulfilled }) {
        let settled: ClaimOutcome;
        try {
          settled = claimOutcome(200, (await queryFulfilled).data);
        } catch (rejection) {
          // `queryFulfilled` rigetta su OGNI risposta non-2xx, e fra queste c'e' il
          // `409 already_claimed` — che per noi e' un successo, e uno dei casi in cui il saldo e'
          // proprio cambiato. Un `catch` che si limitasse a ignorare l'errore rimetterebbe il
          // difetto in piedi nel suo scenario peggiore: la seconda pressione dopo una rete caduta.
          settled = claimOutcomeFromError((rejection as { error?: unknown } | undefined)?.error);
        }
        if (!claimMovedMoney(settled)) return;
        // Invalida il SOLO saldo. Non le leghe (il claim non tocca il punteggio) e non le
        // politiche di cache di qbankApi, che restano quelle di chi possiede il wallet.
        dispatch(qbankApi.util.invalidateTags([QBANK_WALLET_TAG]));
      },
    }),
  }),
});

export const { useGetMissionsQuery, useClaimMissionMutation } = questApi;
